// AI Orchestration — super-admin configurable triage & routing rules.
import { useEffect, useState } from "react";
import { Loader2, Save, Bot, Zap, GitBranch, Plus, Trash2, ShieldAlert, FlaskConical, CheckCircle2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import RelationalPicker from "@/admin/components/RelationalPicker";

const CHANNELS = ["whatsapp", "instagram", "facebook", "telegram", "viber", "webchat", "sms", "email"];
const TONES = [
  { id: "warm-professional", label: "Warm Professional" },
  { id: "formal", label: "Formal" },
  { id: "casual", label: "Casual" },
  { id: "empathetic", label: "Empathetic" },
  { id: "firm-compliance", label: "Firm — Compliance" },
  { id: "concise", label: "Concise" },
];

function ChannelChips({ selected, onToggle, testPrefix, disabled }) {
  return (
    <div className="flex flex-wrap gap-2">
      {CHANNELS.map((c) => {
        const on = (selected || []).includes(c);
        return (
          <button key={c} type="button" disabled={disabled} data-testid={`${testPrefix}-${c}`}
            onClick={() => onToggle(c)}
            className={`text-xs px-3 py-1.5 rounded-full border capitalize transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/50"}`}>
            {c}
          </button>
        );
      })}
    </div>
  );
}

export default function Orchestration() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { canWrite } = useCurrentModulePerms();
  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const [previewQ, setPreviewQ] = useState("");
  const [previewRes, setPreviewRes] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  async function runPreview() {
    if (!previewQ.trim()) return;
    setPreviewing(true); setPreviewRes(null);
    try {
      const r = await api.post("/admin/orchestrator/kb-preview", { question: previewQ });
      setPreviewRes(r.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setPreviewing(false); }
  }

  async function load() {
    setLoading(true);
    try { const r = await api.get("/admin/orchestrator/config"); setCfg(r.data); }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function toggleInList(key, val) {
    const list = cfg[key] || [];
    set({ [key]: list.includes(val) ? list.filter((x) => x !== val) : [...list, val] });
  }

  async function save() {
    setSaving(true);
    try {
      const payload = { ...cfg };
      // normalise keyword textarea → array
      if (typeof payload._human_kw === "string") { payload.human_request_keywords = payload._human_kw.split(",").map((s) => s.trim()).filter(Boolean); delete payload._human_kw; }
      await api.put("/admin/orchestrator/config", payload);
      toast.success("Orchestration config saved");
      load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }

  function addIntentRule() {
    set({ intent_rules: [...(cfg.intent_rules || []), { label: "New Rule", keywords: [], required_skill: "", always_escalate: true }] });
  }
  function updateRule(i, patch) {
    const rules = [...(cfg.intent_rules || [])];
    rules[i] = { ...rules[i], ...patch };
    set({ intent_rules: rules });
  }
  function removeRule(i) { set({ intent_rules: (cfg.intent_rules || []).filter((_, idx) => idx !== i) }); }

  if (loading || !cfg) return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  const humanKw = cfg._human_kw !== undefined ? cfg._human_kw : (cfg.human_request_keywords || []).join(", ");

  return (
    <div className="px-4 sm:px-6 py-6 max-w-4xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="orch-title">
            <Bot className="w-6 h-6 text-primary" /> AI Orchestration
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Intelligent triage, bot answering, and directed queuing across your omnichannel inbox.</p>
        </div>
        {canWrite && <Button data-testid="orch-save" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</Button>}
      </div>

      {/* Master */}
      <Card className={`p-5 mb-5 border-2 ${cfg.enabled ? "border-primary/40" : "border-border"}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display font-bold flex items-center gap-2"><Zap className="w-4 h-4 text-primary" /> Enable Orchestration</div>
            <p className="text-xs text-muted-foreground mt-1">When OFF, inbound messages go to all agents (legacy). When ON, the state machine + claim queue take over.</p>
          </div>
          <Switch data-testid="orch-enabled" checked={!!cfg.enabled} onCheckedChange={(v) => set({ enabled: !!v })} />
        </div>
      </Card>

      {/* Bot answering */}
      <Card className="p-5 mb-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-display font-bold flex items-center gap-2"><Bot className="w-4 h-4 text-primary" /> AI Bot (Gatekeeper)</div>
          <Switch data-testid="orch-bot-enabled" checked={!!cfg.bot_enabled} onCheckedChange={(v) => set({ bot_enabled: !!v })} />
        </div>
        <div>
          <Label className="mb-2 block">Channels the bot answers on</Label>
          <ChannelChips selected={cfg.bot_channels} onToggle={(c) => toggleInList("bot_channels", c)} testPrefix="orch-bch" disabled={!canWrite} />
        </div>
        <div>
          <Label className="mb-2 block">Auto-send channels (reply delivered directly to customer)</Label>
          <p className="text-xs text-muted-foreground mb-2">Channels NOT selected here are draft-only — the bot writes a suggested reply the agent edits & sends. Email is always draft-only.</p>
          <ChannelChips selected={cfg.auto_send_channels} onToggle={(c) => toggleInList("auto_send_channels", c)} testPrefix="orch-asch" disabled={!canWrite} />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Bot tone</Label>
            <Select value={cfg.bot_tone} onValueChange={(v) => set({ bot_tone: v })}>
              <SelectTrigger data-testid="orch-tone" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{TONES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>KB snippets per answer (top-k)</Label><Input data-testid="orch-topk" type="number" min={1} max={8} value={cfg.kb_top_k} onChange={(e) => set({ kb_top_k: Number(e.target.value) })} /></div>
        </div>
        <div><Label>Bot persona / instructions</Label><Textarea data-testid="orch-persona" rows={2} value={cfg.bot_persona || ""} onChange={(e) => set({ bot_persona: e.target.value })} /></div>
      </Card>

      {/* Handover triggers */}
      <Card className="p-5 mb-5 space-y-4">
        <div className="font-display font-bold flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-primary" /> Handover Triggers</div>
        <div className="flex items-center justify-between"><Label>Customer asks for a human</Label><Switch data-testid="orch-t-human" checked={!!cfg.handover_on_human_request} onCheckedChange={(v) => set({ handover_on_human_request: !!v })} /></div>
        <div><Label className="text-xs">Human-request keywords (comma-separated)</Label><Textarea data-testid="orch-human-kw" rows={2} value={humanKw} onChange={(e) => set({ _human_kw: e.target.value })} /></div>
        <div className="flex items-center justify-between border-t pt-3"><Label>Negative / frustrated sentiment</Label><Switch data-testid="orch-t-sentiment" checked={!!cfg.handover_on_negative_sentiment} onCheckedChange={(v) => set({ handover_on_negative_sentiment: !!v })} /></div>
        <div><Label className="text-xs">Sentiment escalate threshold ({Number(cfg.sentiment_negative_threshold).toFixed(2)})</Label><Input data-testid="orch-sent-th" type="number" step="0.05" min={0} max={1} value={cfg.sentiment_negative_threshold} onChange={(e) => set({ sentiment_negative_threshold: Number(e.target.value) })} /></div>
        <div className="flex items-center justify-between border-t pt-3"><Label>Low KB confidence (bot can't answer)</Label><Switch data-testid="orch-t-kb" checked={!!cfg.handover_on_low_kb_confidence} onCheckedChange={(v) => set({ handover_on_low_kb_confidence: !!v })} /></div>
        <div><Label className="text-xs">Minimum KB match score ({Number(cfg.kb_min_score).toFixed(2)})</Label><Input data-testid="orch-kb-min" type="number" step="0.01" min={0} max={1} value={cfg.kb_min_score} onChange={(e) => set({ kb_min_score: Number(e.target.value) })} /></div>
        <div className="border-t pt-3"><Label>Max bot turns before handover</Label><Input data-testid="orch-maxturns" type="number" min={1} max={10} value={cfg.handover_max_bot_turns} onChange={(e) => set({ handover_max_bot_turns: Number(e.target.value) })} className="w-24" /></div>
      </Card>

      {/* KB Match Preview — dry-run the bot to tune the confidence threshold */}
      <Card className="p-5 mb-5 space-y-3" data-testid="orch-kb-preview">
        <div className="font-display font-bold flex items-center gap-2"><FlaskConical className="w-4 h-4 text-primary" /> KB Match Preview</div>
        <p className="text-xs text-muted-foreground">Type a sample customer question to see the bot's answer and KB match score — and whether it would auto-answer or escalate at the current threshold ({Number(cfg.kb_min_score).toFixed(2)}).</p>
        <div className="flex gap-2">
          <Input data-testid="orch-preview-input" value={previewQ} onChange={(e) => setPreviewQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runPreview(); }}
            placeholder="e.g. What are your support hours?" />
          <Button data-testid="orch-preview-btn" onClick={runPreview} disabled={previewing || !previewQ.trim()} className="shrink-0 gap-1.5">
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />} Test
          </Button>
        </div>
        {previewRes && (
          <div className="space-y-3 pt-1" data-testid="orch-preview-result">
            <div className={`flex items-center gap-2 text-sm font-bold px-3 py-2 rounded-lg ${previewRes.verdict === "answer" ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300" : "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300"}`}>
              {previewRes.verdict === "answer"
                ? <><CheckCircle2 className="w-4 h-4" /> Bot would auto-answer</>
                : <><ArrowUpRight className="w-4 h-4" /> Would escalate to a human {previewRes.verdict_reason === "bot_unavailable" ? "(LLM offline)" : "(low KB confidence)"}</>}
            </div>
            {previewRes.is_mocked && <div className="text-xs text-red-600 dark:text-red-400">⚠ LLM unavailable — configure a provider key in AI Suite or set EMERGENT_LLM_KEY.</div>}
            <div>
              <Label className="text-xs">Bot answer {previewRes.model ? <span className="text-muted-foreground font-normal">· {previewRes.provider}/{previewRes.model}</span> : null}</Label>
              <div className="text-sm bg-muted/40 rounded-lg p-3 mt-1 whitespace-pre-wrap">{previewRes.answer || "—"}</div>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span>Top match score: <span className={`font-bold ${previewRes.top_score >= previewRes.kb_min_score ? "text-emerald-600" : "text-amber-600"}`}>{previewRes.top_score}</span></span>
              <span className="text-muted-foreground">Threshold: {previewRes.kb_min_score}</span>
            </div>
            {(previewRes.citations || []).length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs">KB matches</Label>
                {previewRes.citations.map((c, i) => (
                  <div key={i} className="text-xs bg-muted/30 rounded-md px-2.5 py-1.5" data-testid={`orch-preview-cite-${i}`}>
                    <div className="flex justify-between gap-2"><span className="font-medium truncate">{c.title}</span><span className="font-bold shrink-0">{c.score}</span></div>
                    <div className="text-muted-foreground truncate">{c.snippet}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Skill routing */}
      <Card className="p-5 mb-5 space-y-3">
        <div className="font-display font-bold flex items-center gap-2"><GitBranch className="w-4 h-4 text-primary" /> Channel → Skill Routing</div>
        <p className="text-xs text-muted-foreground">Route escalated chats to specialists. Skills come from your <span className="font-medium">Skills</span> catalog and match agents' skill tags — an agent only sees a pending chat if they hold one of the required skills.</p>
        <div className="grid sm:grid-cols-2 gap-x-5 gap-y-3">
          {CHANNELS.map((ch) => (
            <div key={ch} className="flex items-center gap-3">
              <span className="text-sm capitalize w-24 shrink-0">{ch}</span>
              <div className="flex-1">
                <RelationalPicker
                  kind="skills" multi returnKey="name" testId={`orch-chskill-${ch}`}
                  placeholder="Any agent"
                  value={(cfg.channel_skill_map || {})[ch] || []}
                  onChange={(v) => set({ channel_skill_map: { ...(cfg.channel_skill_map || {}), [ch]: v } })}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Intent rules */}
      <Card className="p-5 mb-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-display font-bold flex items-center gap-2"><GitBranch className="w-4 h-4 text-primary" /> Intent Rules → Escalate + Route</div>
          {canWrite && <Button size="sm" variant="outline" data-testid="orch-add-rule" onClick={addIntentRule} className="gap-1"><Plus className="w-3.5 h-3.5" /> Add rule</Button>}
        </div>
        <p className="text-xs text-muted-foreground">If a customer message contains any keyword, the conversation is escalated to a human (optionally routed to a skill).</p>
        {(cfg.intent_rules || []).map((r, i) => (
          <div key={i} className="grid sm:grid-cols-[1fr_2fr_1.2fr_auto] gap-2 items-center bg-muted/30 rounded-lg p-2" data-testid={`orch-rule-${i}`}>
            <Input placeholder="Label" value={r.label} onChange={(e) => updateRule(i, { label: e.target.value })} />
            <Input placeholder="keywords, comma-separated" value={(r.keywords || []).join(", ")} onChange={(e) => updateRule(i, { keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            <RelationalPicker kind="skills" returnKey="name" testId={`orch-rule-skill-${i}`} placeholder="Route to skill (optional)"
              value={r.required_skill || ""} onChange={(v) => updateRule(i, { required_skill: v })} />
            {canWrite && <Button size="sm" variant="ghost" className="text-destructive" data-testid={`orch-rule-del-${i}`} onClick={() => removeRule(i)}><Trash2 className="w-4 h-4" /></Button>}
          </div>
        ))}
        {(cfg.intent_rules || []).length === 0 && <p className="text-xs text-muted-foreground italic">No intent rules.</p>}
      </Card>
    </div>
  );
}
